import { OrderStorage } from "@/storage/OrderStorage";

import Drink from "@/containers/Product/Drink";
import Step from "@/containers/Product/Step";

import { OMath } from "@/tools/OMath";
import EcleticaStep from "./EcleticaStep";
import { EnvironmentConfiguration } from "@/core/EnvironmentConfiguration";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { Button, Drawer } from "@/components";

import styles from "@/styles/containers/product-details.module.scss";
import { ChallengeDrawer } from "../ChallengeDrawer";
import { EcleticaTable } from "@/services/Ecletica/EcleticaTable";
import { NetLogicTable } from "@/services/NetLogic";
import { ColibriTable } from "@/services/Colibri";
import { CatalogHelpers } from "@/tools";
import { CheckSuccess } from "@/components/Icons";
import Bugsnag from "@bugsnag/js";
import { Alert } from "@/components/Alert";
import { TableException } from "@/services/expections/TableException";
import { SaiposTable } from "@/services/Saipos";

export default function ProductDetails({ product, onClose, cartItemIndex }) {
  const appConfigurations = EnvironmentConfiguration.fromStorage();

  const [sendingOrder, setSendingOrder] = useState(false);
  const [sentOrder, setSentOrder] = useState(false);
  const [openChallange, setOpenChallenge] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  const calculateItemPrice = (item, totalValue) => {
    const props = ["items", "sizes", "extra_ingredients", "doughs", "edges"];

    let total =
      Math.round((item.price || 0) * 100) *
      (item.selected !== false && item.quantity > 0 ? 1 : 0);
    for (const key of props) {
      if (item[key] && item[key].length > 0) {
        for (const subItem of item[key]) {
          total += calculateItemPrice(subItem) * 100;
        }
      }
    }

    if (totalValue)
      return +OMath.div(OMath.times(total, item.quantity), 100).toFixed(2);

    return +OMath.div(total, 100).toFixed(2);
  };

  const onSave = (product) => {
    if (cartItemIndex !== null) {
      OrderStorage.updateCartItem(cartItemIndex, product);
    } else {
      OrderStorage.addItemToCart(product);
    }

    onClose && onClose();
  };

  const Categories = {
    DRINK: "drink",
    STEP: "step",
    PIZZA: "pizza",
    FLAVOR: "flavor",
  };

  const isEcleticaCombination =
    appConfigurations?.tableService === "ECLETICA" && product?.type == "step";

  const t = useTranslations();

  const onSendOrder = async (skip = false) => {
    
    if (appConfigurations?.allowChallenge === true && !skip)
      return setOpenChallenge(true);
    
    const order = OrderStorage.getOrder();

    const cartItems = order?.cart?.catalog?.items;
    if (cartItems?.length <= 0) {
      setError(t("emptyCart"));
      setSendingOrder(false);
      setShowError(true);
      return;
    }

    setSendingOrder(true);
    setOpenChallenge(false);

    order.cart.catalog = CatalogHelpers.clearUnselectedItems(
      order.cart.catalog
    );

    try {
      if (appConfigurations?.tableService === "ECLETICA") {
        const ecleticaTable = new EcleticaTable();
        await ecleticaTable.addItems(cartItems);
      } else if (appConfigurations?.tableService === "NETLOGIC") {
        const netlogicTable = new NetLogicTable();
        await netlogicTable.addItems(order);
      } else if (appConfigurations?.tableService === "COLIBRI") {
        const colibriTable = new ColibriTable();
        await colibriTable.addItems(order);
      } else if (appConfigurations?.tableService === "SAIPOS") {
        const saiposTable = new SaiposTable();
        await saiposTable.addItems(order);
      } else {
        console.error(
          "Inclusão de itens da integração de mesa não implementada"
        );
        return;
      }

      setError(null);
      setShowError(false);

      OrderStorage.clearCartItems();
      setSentOrder(true);
    } catch (err) {
      console.log("err", err.message);
      Bugsnag.notify(err);

      if (err instanceof TableException) {
        setError(t(err.template, err.variables));
        setSentOrder(false);
        setShowError(true);
        setSendingOrder(false);
        return;
      }

      setError(err.message || t("unableToAddItemsToTable"));
      setSentOrder(false);
      setShowError(true);
    }

    // setSendingOrder(false);
  };

  return (
    <>
      {[Categories.DRINK].includes(product.type) && (
        <Drink
          isUpdate={cartItemIndex !== null}
          onSave={(e) => onSave(e)}
          product={product}
          onClose={onClose}
          cartItemIndex={cartItemIndex}
        />
      )}

      {[Categories.STEP].includes(product.type) && !isEcleticaCombination && (
        <Step
          isUpdate={cartItemIndex !== null}
          onSave={(e) => onSave(e)}
          product={product}
          onClose={onClose}
          onSendOrder={() => onSendOrder()}
          cartItemIndex={cartItemIndex}
        />
      )}

      {[Categories.STEP].includes(product.type) && isEcleticaCombination && (
        <EcleticaStep
          isUpdate={cartItemIndex !== null}
          onSave={(e) => onSave(e)}
          product={product}
          onClose={onClose}
          onSendOrder={() => onSendOrder()}
          cartItemIndex={cartItemIndex}
        />
      )}

      {/* {[Categories.PIZZA, Categories.FLAVOR].includes(product.type) && (
        <Pizza
          isUpdate={cartItemIndex !== null}
          onSave={(e) => onSave(e)}
          product={product}
          onClose={onClose}
        />
      )} */}

      <Drawer
        open={sendingOrder}
        onClose={() => setSendingOrder(false)}
        title={sentOrder ? t("orderSentToKitchen2") : t("sendingToKitchen")}
        direction="bottom-center"
      >
        {!sentOrder && (
          <div className={styles.sendingOrder}>
            <>
              <div className={styles.statusContainer}>
                <p className={styles.msgOne}>{t("receivingYourOrder")}</p>
                <p className={styles.msgTwo}>{t("pleaseWaitAlmostThere")}</p>
              </div>

              <span className={styles.loader} />
            </>
          </div>
        )}

        {sentOrder && (
          <div className={styles.sentOrder}>
            <div className={styles.success}>
              <CheckSuccess />
            </div>
            <p>{t("orderSentToKitchen")}</p>
            <div className={styles.close}>
              <Button
                design="default"
                className="w-100"
                onClick={() => {
                  setSendingOrder(false);
                  onClose();
                  OrderStorage.clearCartItems();
                }}
              >
                {t("close")}
              </Button>
            </div>
          </div>
        )}
      </Drawer>

      <Alert
        open={showError}
        onClose={() => setShowError(false)}
        title={t("tryAgain")}
        subtitle={error}
      />

      {openChallange && (
        <ChallengeDrawer
          onClose={() => {
            setOpenChallenge(false);
          }}
          onConfirm={() => {
            setOpenChallenge(false);
            onSendOrder(true);
          }}
        />
      )}
    </>
  );
}
